import { useEffect, useState } from "react";

import { cn } from "@btnet/utilities-core";

import "./BuiFloatingActionBar.less";
import { type IBuiFloatingActionBarProps } from "./BuiFloatingActionBar.types";

/**
 * BuiFloatingActionBar - the blueprint (BUI) floating action bar primitive.
 *
 * Renders a pill-shaped bar fixed to the bottom-center of the viewport that
 * slides up/fades in when opened and slides down/fades out when closed. It is a
 * "dumb" positioning + animation primitive: it owns no selection, action, or
 * overflow logic and carries no domain knowledge. Compose it with actions in the
 * app-facing `BTFloatingActionBar` (`@btnet/core-components`) instead of using it
 * directly in app code.
 *
 * While closed (and after its exit animation completes) the bar renders nothing,
 * so it stays out of the accessibility tree when there is nothing to act on.
 *
 * @param props - {@link IBuiFloatingActionBarProps} controlling visibility, content, and styling.
 * @returns The rendered bar, or `null` while fully closed.
 * @example
 * <BuiFloatingActionBar isOpen={selectedCount > 0} testid="bill-fab">
 *   <span>{selectedCount} selected</span>
 *   <BuiButton testid="delete" type="tertiary">Delete</BuiButton>
 * </BuiFloatingActionBar>
 */
export const BuiFloatingActionBar = ({
  isOpen,
  children,
  ariaLabel = "Actions",
  className,
  style,
  testid,
}: IBuiFloatingActionBarProps) => {
  // Keep the bar mounted through its exit animation: `isOpen` flips to false
  // immediately, but we only unmount once the out-animation has finished.
  const [isMounted, setIsMounted] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
    }
  }, [isOpen]);

  const handleAnimationEnd = (): void => {
    if (!isOpen) {
      setIsMounted(false);
    }
  };

  if (!isMounted && !isOpen) {
    return null;
  }

  return (
    <div
      role="toolbar"
      aria-label={ariaLabel}
      data-testid={testid}
      className={cn(
        "fixed bottom-12 left-1/2 z-40 flex -translate-x-1/2 items-center gap-4 rounded-md border border-blue-80 bg-blue-100 px-5 py-2 font-sans text-sm font-normal text-blue-5 shadow-floating",
        {
          "bui-floating-action-bar--in-animation": isOpen,
          "bui-floating-action-bar--out-animation": !isOpen,
        },
        className,
      )}
      style={style}
      onAnimationEnd={handleAnimationEnd}
    >
      {children}
    </div>
  );
};
